import { VueConstructor } from "vue";
import { format, formatRelative, parseISO, differenceInDays, Locale } from "date-fns";
import { nl } from "date-fns/locale";

const relativeTimeStrips = [/ om [0-9]+:[0-9+].*/gi, / at [0-9]+:[0-9+].*/gi];

function createDateFilter(
  defaultFormat?: string,
  defaultOptions?: {
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    firstWeekContainsDate?: number;
    useAdditionalWeekYearTokens?: boolean;
    useAdditionalDayOfYearTokens?: boolean;
  },
  relativeVersion = true
) {
  return function dateFilter(
    date: Date,
    dateFormat?: string,
    opts?: {
      locale?: Locale;
      weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
      firstWeekContainsDate?: number;
      useAdditionalWeekYearTokens?: boolean;
      useAdditionalDayOfYearTokens?: boolean;
    },
    daysDiffForShowingRelative = relativeVersion ? 7 : 0,
    stripRelativeTime = false
  ) {
    if (!format && !defaultFormat) {
      throw new Error(
        "Date format must be specified. Provide a custom format calling date(new Date(), 'd MM yyyy') or create own date filter with predefined format."
      );
    }

    if (!date) {
      return "";
    }

    if (typeof date == "string") {
      date = parseISO(date);
    }

    const defaultOptsClone = Object.assign({}, defaultOptions || {});
    const currentOpts = Object.assign(defaultOptsClone, opts);

    if (daysDiffForShowingRelative > 0) {
      const diffDays = differenceInDays(date, new Date());
      if (diffDays < daysDiffForShowingRelative && diffDays > -daysDiffForShowingRelative) {
        let relative = formatRelative(date, new Date(), currentOpts);

        //first check if it didn't fallback to 'other', then we have to use our own formatting
        if (relative != format(date, nl.formatRelative("other"), currentOpts)) {
          if (stripRelativeTime) {
            for (let i = 0; i < relativeTimeStrips.length; i++) {
              relative = relative.replace(relativeTimeStrips[i], "");
            }
          }
          return relative;
        }
      }
    }
    return format(date, dateFormat || defaultFormat || "Pp", currentOpts);
  };
}

function install(
  Vue: VueConstructor,
  defaultFormat?: string,
  defaultOptions: {
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    firstWeekContainsDate?: number;
    useAdditionalWeekYearTokens?: boolean;
    useAdditionalDayOfYearTokens?: boolean;
  } = {}
) {
  if (!defaultFormat) {
    //const formatLong: any = nl.formatLong;
    //defaultFormat = formatLong.dateTime();
    defaultFormat = "PPPPp";
  }
  if (!defaultOptions.locale) {
    defaultOptions.locale = nl;
  }
  const dateFilter = createDateFilter(defaultFormat, defaultOptions, false);
  const relativeDateFilter = createDateFilter(defaultFormat, defaultOptions);

  Vue.filter("date", dateFilter);
  Vue.filter("relativeDate", relativeDateFilter);

  Vue.mixin({
    methods: {
      $date: dateFilter,
      $relativeDate: relativeDateFilter
    }
  });
}

export default install;
